import { omit } from 'ramda';

import { getPageData } from '@/app/l/[region]/[language]/course/[course]/[[...extraParams]]/course.queries';
import { type CoursePageParams } from '@/app/l/[region]/[language]/course/[course]/[[...extraParams]]/page';
import { type FullParams, type PageType } from '@/app/urls/url-mapping';
import { type RemoteTimezone } from '@/lib/utils/dates';
import {
  type AvailableCourseTrack,
  type PartialSupportCourseTrack,
  type UrlEntry,
  type UrlRegion,
} from '@/types';
import { type PageParams, type PdpParamsParsed } from './page';

export const canonicalUrlArgs = async (
  pageType: PageType,
  params: FullParams
): Promise<[PageType, FullParams]> => {
  const courseParams = omit(['campus'], params) as CoursePageParams;
  const pageData = await getPageData(courseParams);

  // Use course page as canonical url if it contains PDP content
  return pageData.coursePage?.cohortSelector
    ? [`course/${courseParams.course}`, courseParams]
    : [pageType, params];
};

export type PdpTrack = AvailableCourseTrack;

export type PdpPages = `pdp/${PartialSupportCourseTrack}/rmt` | 'pdp/ai/ber';

export const urls: Record<PdpPages, Partial<Record<UrlRegion, UrlEntry>>> = {
  // AI Consulting
  'pdp/ac/rmt': {
    de: 'ki-consulting-integration/remote',
    'de-en': 'ai-consulting-integration/remote',
    es: 'consultor-ia/remoto',
    'es-en': 'ai-consulting-integration/remote',
    fr: 'consulting-ia/a-distance',
    'fr-en': 'ai-consulting-integration/remote',
    gb: 'ai-consulting-integration/remote',
    nl: 'ai-consulting-integratie/remote',
    'nl-en': 'ai-consulting-integration/remote',
    pt: 'consultoria-integracao-ia/remoto',
    'pt-en': 'ai-consulting-integration/remote',
    ww: 'ai-consulting-integration/remote',
  },

  // Artificial Intelligence
  'pdp/ai/ber': {
    de: 'kunstliche-intelligenz/berlin',
    'de-en': 'artificial-intelligence/berlin',
  },
  'pdp/ai/rmt': {
    de: 'kunstliche-intelligenz/remote',
    'de-en': 'artificial-intelligence/remote',
    es: 'inteligencia-artificial/remoto',
    'es-en': 'artificial-intelligence/remote',
    fr: 'intelligence-artificielle/a-distance',
    'fr-en': 'artificial-intelligence/remote',
    gb: 'artificial-intelligence/remote',
    nl: 'kunstmatige-intelligentie/remote',
    'nl-en': 'artificial-intelligence/remote',
    pt: 'inteligencia-artificial/remoto',
    'pt-en': 'artificial-intelligence/remote',
    ww: 'artificial-intelligence/remote',
  },

  // Cloud Engineering
  'pdp/ce/rmt': {
    de: 'cloud-engineer/remote',
    'de-en': 'cloud-engineer/remote',
    es: 'cloud-engineer/remoto',
    'es-en': 'cloud-engineer/remote',
    fr: 'ingenieur-cloud/a-distance',
    'fr-en': 'cloud-engineer/remote',
    gb: 'cloud-engineer/remote',
    nl: 'cloud-engineer/remote',
    'nl-en': 'cloud-engineer/remote',
    pt: 'engenharia-cloud/remoto',
    'pt-en': 'cloud-engineer/remote',
    ww: 'cloud-engineer/remote',
  },

  // Data Engineering
  'pdp/de/rmt': {
    de: 'data-engineering/remote',
    'de-en': 'data-engineering/remote',
    es: 'data-engineering/remoto',
    'es-en': 'data-engineering/remote',
    fr: 'data-engineer/a-distance',
    'fr-en': 'data-engineering/remote',
    gb: 'data-engineering/remote',
    nl: 'data-engineering/remote',
    'nl-en': 'data-engineering/remote',
    pt: 'engenharia-dados/remoto',
    'pt-en': 'data-engineering/remote',
    ww: 'data-engineering/remote',
  },

  // Devops & Cloud Computing
  'pdp/dv/rmt': {
    de: 'devops-cloud-computing/remote',
    'de-en': 'devops-cloud-computing/remote',
    es: 'devops-cloud-computing/remoto',
    'es-en': 'devops-cloud-computing/remote',
    fr: 'devops-cloud-computing/a-distance',
    'fr-en': 'devops-cloud-computing/remote',
    gb: 'devops-cloud-computing/remote',
    nl: 'devops-cloud-computing/remote',
    'nl-en': 'devops-cloud-computing/remote',
    pt: 'devops-cloud-computing/remoto',
    'pt-en': 'devops-cloud-computing/remote',
    ww: 'devops-cloud-computing/remote',
  },

  // Digital Marketing
  'pdp/mk/rmt': {
    de: 'digital-marketing/remote',
    'de-en': 'digital-marketing/remote',
    es: 'marketing-digital/remoto',
    'es-en': 'digital-marketing/remote',
    fr: 'marketing-digital/a-distance',
    'fr-en': 'digital-marketing/remote',
    gb: 'digital-marketing/remote',
    nl: 'digital-marketing/remote',
    'nl-en': 'digital-marketing/remote',
    pt: 'marketing-digital/remoto',
    'pt-en': 'digital-marketing/remote',
    ww: 'digital-marketing/remote',
  },

  // Data Science & Machine Learning
  'pdp/ml/rmt': {
    de: 'data-science-machine-learning/remote',
    'de-en': 'data-science-machine-learning/remote',
    es: 'data-science-machine-learning/remoto',
    'es-en': 'data-science-machine-learning/remote',
    fr: 'data-science-machine-learning/a-distance',
    'fr-en': 'data-science-machine-learning/remote',
    gb: 'data-science-machine-learning/remote',
    nl: 'data-science-machine-learning/remote',
    'nl-en': 'data-science-machine-learning/remote',
    pt: 'data-science-machine-learning/remoto',
    'pt-en': 'data-science-machine-learning/remote',
    ww: 'data-science-machine-learning/remote',
  },

  // AI Product Management
  'pdp/pm/rmt': {
    de: 'ki-product-manager/remote',
    'de-en': 'ai-product-manager/remote',
    es: 'product-manager-ia/remoto',
    'es-en': 'ai-product-manager/remote',
    fr: 'product-manager-ia/a-distance',
    'fr-en': 'ai-product-manager/remote',
    gb: 'ai-product-manager/remote',
    nl: 'product-manager-ai/remote',
    'nl-en': 'ai-product-manager/remote',
    pt: 'product-manager-ia/remoto',
    'pt-en': 'ai-product-manager/remote',
    ww: 'ai-product-manager/remote',
  },
};

export const getPdpType = (
  params: Pick<PageParams, 'campus' | 'course'>
): PdpPages => {
  const { campus, course } = params;
  return `pdp/${course}/${campus}` as PdpPages;
};

export const parseParams = (params: PageParams) => {
  const { extraParams = [], ...initialParsedParams } = params;

  return (extraParams as string[]).reduce<PdpParamsParsed>(
    (parsedParams, pathPart, index) => {
      switch (pathPart) {
        case 'timezone': {
          return {
            ...parsedParams,
            timezone: extraParams[index + 1].replace(
              '-',
              '/'
            ) as RemoteTimezone,
          };
        }

        case 'e': {
          return {
            ...parsedParams,
            exprimentId: extraParams[index + 1],
            variantId: extraParams[index + 2],
          };
        }

        case 'user-country': {
          return {
            ...parsedParams,
            userCountry: extraParams[index + 1],
          };
        }

        default: {
          return parsedParams;
        }
      }
    },
    initialParsedParams
  );
};
